import React from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { HelmetProvider } from 'react-helmet-async';
import { SiteSettingsProvider } from './contexts/SiteSettingsContext';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { AuthProvider } from './hooks/useAuth';
import Navbar from './components/Navbar';
import Footer from './components/Footer';
import ProtectedRoute from './components/ProtectedRoute';

// Pages
import HomePage from './pages/HomePage';
import LoginPage from './pages/LoginPage';
import RegisterPage from './pages/RegisterPage';
import ServicesPage from './pages/ServicesPage';
import ProductsPage from './pages/ProductsPage';
import ServiceDetailPage from './pages/ServiceDetailPage';
import ProductDetailPage from './pages/ProductDetailPage';
import BookingPage from './pages/BookingPage';
import ProvidersPage from './pages/ProvidersPage';
import ProviderProfilePage from './pages/ProviderProfilePage';
import DashboardPage from './pages/DashboardPage';
import BookingsListPage from './pages/BookingsListPage';
import MessagesPage from './pages/MessagesPage';
import NotificationsPage from './pages/NotificationsPage';
import ProfilePage from './pages/ProfilePage';
import CreateServicePage from './pages/CreateServicePage';
import CreateProductPage from './pages/CreateProductPage';
import AdminDashboardPage from './pages/AdminDashboardPage';
import AdminLoginPage from './pages/AdminLoginPage';
import OrdersPage from './pages/OrdersPage';
import FavoritesPage from './pages/FavoritesPage';
import SubscriptionPage from './pages/SubscriptionPage';
import HelpCenterPage from './pages/HelpCenterPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import SafetyTipsPage from './pages/SafetyTipsPage';
import ContactPage from './pages/ContactPage';
import CompleteProfilePage from './pages/CompleteProfilePage';

function AppLayout() {
  const location = useLocation();
  const isAdmin = location.pathname.startsWith('/ahupa');

  return (
    <div className="flex flex-col min-h-screen">
      {!isAdmin && <Navbar />}
      <main className={isAdmin ? '' : 'flex-1'}>
        <Routes>
              {/* Public Routes */}
              <Route path="/" element={<HomePage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/register" element={<RegisterPage />} />
              <Route path="/services" element={<ServicesPage />} />
              <Route path="/services/:id" element={<ServiceDetailPage />} />
              <Route path="/products" element={<ProductsPage />} />
              <Route path="/products/:id" element={<ProductDetailPage />} />
              <Route path="/providers" element={<ProvidersPage />} />
              <Route path="/providers/:id" element={<ProviderProfilePage />} />
              <Route path="/help" element={<HelpCenterPage />} />
              <Route path="/terms" element={<TermsPage />} />
              <Route path="/privacy" element={<PrivacyPage />} />
              <Route path="/safety" element={<SafetyTipsPage />} />
              <Route path="/contact" element={<ContactPage />} />
              <Route path="/complete-profile" element={<CompleteProfilePage />} />

              {/* Authenticated Routes */}
              <Route path="/dashboard" element={
                <ProtectedRoute><DashboardPage /></ProtectedRoute>
              } />
              <Route path="/bookings" element={
                <ProtectedRoute><BookingsListPage /></ProtectedRoute>
              } />
              <Route path="/book/:serviceId" element={
                <ProtectedRoute><BookingPage /></ProtectedRoute>
              } />
              <Route path="/messages" element={
                <ProtectedRoute><MessagesPage /></ProtectedRoute>
              } />
              <Route path="/messages/:conversationId" element={
                <ProtectedRoute><MessagesPage /></ProtectedRoute>
              } />
              <Route path="/notifications" element={
                <ProtectedRoute><NotificationsPage /></ProtectedRoute>
              } />
              <Route path="/profile" element={
                <ProtectedRoute><ProfilePage /></ProtectedRoute>
              } />
              <Route path="/orders" element={
                <ProtectedRoute><OrdersPage /></ProtectedRoute>
              } />
              <Route path="/favorites" element={
                <ProtectedRoute><FavoritesPage /></ProtectedRoute>
              } />
              <Route path="/subscription" element={
                <ProtectedRoute><SubscriptionPage /></ProtectedRoute>
              } />

              {/* Provider Routes */}
              <Route path="/services/create" element={
                <ProtectedRoute><CreateServicePage /></ProtectedRoute>
              } />
              <Route path="/services/edit/:id" element={
                <ProtectedRoute><CreateServicePage /></ProtectedRoute>
              } />
              <Route path="/products/create" element={
                <ProtectedRoute><CreateProductPage /></ProtectedRoute>
              } />
              <Route path="/products/edit/:id" element={
                <ProtectedRoute><CreateProductPage /></ProtectedRoute>
              } />

              {/* Admin Routes */}
              <Route path="/ahupa/login" element={<AdminLoginPage />} />
              <Route path="/ahupa" element={
                <ProtectedRoute roles={['admin']} adminRoute><AdminDashboardPage /></ProtectedRoute>
              } />

              {/* 404 */}
              <Route path="*" element={
                <div className="min-h-screen flex items-center justify-center bg-gray-50/50">
                  <div className="text-center">
                    <h1 className="text-7xl font-bold text-gradient mb-4">404</h1>
                    <p className="text-xl text-gray-600 mb-8">Page not found</p>
                    <a href="/" className="bg-gradient-to-r from-primary-600 to-primary-700 text-white px-8 py-3 rounded-2xl font-medium hover:shadow-lg hover:shadow-primary-500/20 transition-all">
                      Go Home
                    </a>
                  </div>
                </div>
              } />
          </Routes>
      </main>
      {!isAdmin && <Footer />}
    </div>
  );
}

function App() {
  return (
    <HelmetProvider>
    <SiteSettingsProvider>
    <GoogleOAuthProvider clientId="1013214230812-39nmqmv2gpeham3235quotkgfm3e0c6t.apps.googleusercontent.com">
    <AuthProvider>
      <Router>
        <AppLayout />
      </Router>
    </AuthProvider>
    </GoogleOAuthProvider>
    </SiteSettingsProvider>
    </HelmetProvider>
  );
}

export default App;
